<template>
  <button
    class="btn mb-0 soft-button" 
    :class="getClasses(variant, color, size, fullWidth, active)"
    :disabled="isDisabled"
  >
    <span class="button-content">
      <slot />
    </span>
  </button>
</template>

<script>
export default {
  name: "SoftButton",
  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "success",
    },
    size: {
      type: String,
      default: "md",
    },
    variant: {
      type: String,
      default: "fill",
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getClasses: (variant, color, size, fullWidth, active) => {
      let colorValue, sizeValue, fullWidthValue, activeValue;

      // Setting the button variant and color
      if (variant === "gradient") {
        colorValue = `bg-gradient-${color}`;
      } else if (variant === "outline") {
        colorValue = `btn-outline-${color}`;
      } else {
        colorValue = `btn-${color}`;
      }

      sizeValue = size ? `btn-${size}` : null;

      fullWidthValue = fullWidth ? `w-100` : null;

      activeValue = active ? `active` : null;

      return `${colorValue} ${sizeValue} ${fullWidthValue} ${activeValue}`;
    },
  },
};
</script>

<style scoped>
.soft-button {
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.soft-button:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.soft-button:not(:disabled):active {
  transform: translateY(0);
  transition: all 0.1s ease;
}

.soft-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.button-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Ripple effect */
.soft-button::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  z-index: 1;
}

.soft-button:not(:disabled):active::before {
  width: 300px;
  height: 300px;
}

/* Dark mode enhancements */
body.dark-mode-active .soft-button {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

body.dark-mode-active .soft-button:not(:disabled):hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

body.dark-mode-active .btn-success {
  background: linear-gradient(135deg, var(--success-600, #059669) 0%, var(--success-500, #10b981) 100%) !important;
  border-color: var(--success-600, #059669) !important;
  color: white !important;
}

body.dark-mode-active .btn-success:hover {
  background: linear-gradient(135deg, var(--success-700, #047857) 0%, var(--success-600, #059669) 100%) !important;
  border-color: var(--success-700, #047857) !important;
}

body.dark-mode-active .btn-primary {
  background: linear-gradient(135deg, var(--primary-600, #2563eb) 0%, var(--primary-500, #3b82f6) 100%) !important;
  border-color: var(--primary-600, #2563eb) !important;
  color: white !important;
}

body.dark-mode-active .btn-primary:hover {
  background: linear-gradient(135deg, var(--primary-700, #1d4ed8) 0%, var(--primary-600, #2563eb) 100%) !important;
  border-color: var(--primary-700, #1d4ed8) !important;
}

body.dark-mode-active .btn-outline-primary {
  background: rgba(59, 130, 246, 0.1) !important;
  border-color: var(--primary-500, #3b82f6) !important;
  color: var(--primary-400, #60a5fa) !important;
}

body.dark-mode-active .btn-outline-primary:hover {
  background: rgba(59, 130, 246, 0.2) !important;
  border-color: var(--primary-400, #60a5fa) !important;
  color: var(--primary-300, #93c5fd) !important;
}

/* Size variations */
.btn-sm.soft-button {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 8px !important;
}

.btn-lg.soft-button {
  padding: 0.75rem 1.5rem;
  font-size: 1.125rem;
  border-radius: 16px !important;
}

/* Focus states for accessibility */
.soft-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

body.dark-mode-active .soft-button:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .soft-button,
  .soft-button::before {
    transition: none;
  }
  
  .soft-button:hover {
    transform: none;
  }
}
</style>
